"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Users, FileSpreadsheet } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface CredibilityPillar {
  id: number;
  iconName: "methodology" | "independence" | "readership";
  tag: string;
  title: string;
  description: string;
  metric: string;
  metricLabel: string;
}
const pillars: CredibilityPillar[] = [{
  id: 1,
  iconName: "methodology",
  tag: "Methodology",
  title: "Rigorous research standards",
  description: "Every report draws on primary industry surveys, verified financial data, and peer-reviewed quantitative analysis.",
  metric: "100%",
  metricLabel: "Primary sourced data"
}, {
  id: 2,
  iconName: "independence",
  tag: "Integrity",
  title: "Zero sponsored bias",
  description: "Our publications operate under strict editorial separation. We accept no paid vendor placements or sponsored rankings.",
  metric: "0",
  metricLabel: "Paid placement influence"
}, {
  id: 3,
  iconName: "readership",
  tag: "Audience",
  title: "Executive readership",
  description: "Researched and structured specifically for boardroom discussions, executive planning, and high-impact capital allocation.",
  metric: "84%",
  metricLabel: "Senior executive decision-makers"
}];
export default function Credibility() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-minfomedia" data-nav="light" className="relative w-full border-b border-border bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Editorial Masthead Rule */}
        <div className="mb-10 w-full border-b border-border pb-1">
          <div className="w-full border-b border-border/40 pb-3">
            <span className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              Publication Standards & Methodology
            </span>
          </div>
        </div>

        {/* Top Split Layout: Narrative & Mission */}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 items-start">
          <div className="lg:col-span-6">
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-[2.5rem] lg:leading-[1.2]">
              Built on editorial independence and verifiable research.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              MinfoMedia exists to provide senior corporate leaders, analysts, and strategists with
              clear, uncompromised intelligence. In an era saturated with paid content marketing,
              we adhere to the traditional standards of institutional publishing.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild variant="outline" className="rounded-sm border-primary px-6 py-3.5 text-primary hover:bg-primary/5">
                <Link to="/AboutUsPage">
                  Read our editorial charter
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <CheckCircle2 className="size-4 text-accent" />
                <span>GDPR & Privacy Compliant</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="rounded-md border border-border bg-card p-6 shadow-xs sm:p-8">
              <div className="border-b border-border pb-4">
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Our Editorial Covenant
                </p>
                <h3 className="mt-1 font-serif text-xl font-semibold text-foreground">
                  Why business leaders rely on our insights
                </h3>
              </div>
              <ul className="mt-6 space-y-4 text-sm leading-relaxed text-muted-foreground">
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                    <span className="font-mono text-xs font-semibold">1</span>
                  </div>
                  <div>
                    <strong className="text-foreground">Uncompromised by sponsorship:</strong> Editorial
                    staff retain complete jurisdiction over report conclusions and analytical
                    frameworks.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                    <span className="font-mono text-xs font-semibold">2</span>
                  </div>
                  <div>
                    <strong className="text-foreground">Accessible for decision-makers:</strong> Complex
                    quantitative metrics distilled into clean executive summaries without losing nuance.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
                    <span className="font-mono text-xs font-semibold">3</span>
                  </div>
                  <div>
                    <strong className="text-foreground">Safe data protocols:</strong> Subscriptions and
                    document access respect reader confidentiality with no third-party data broker
                    reselling.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Credibility Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {pillars.map((pillar, index) => <motion.div key={pillar.id} data-index={index} variants={itemVariants} className="flex">
              <Card className="flex w-full flex-col justify-between rounded-md border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-250 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="p-0">
                  <div className="flex items-center justify-between">
                    <span className="rounded-sm border border-border bg-muted/60 px-2 py-0.5 font-mono text-[11px] text-muted-foreground uppercase">
                      {pillar.tag}
                    </span>
                    <div className="flex size-8 items-center justify-center rounded-md bg-secondary/50 text-foreground">
                      {pillar.iconName === "methodology" && <FileSpreadsheet className="size-4.5 text-foreground" />}
                      {pillar.iconName === "independence" && <ShieldCheck className="size-4.5 text-foreground" />}
                      {pillar.iconName === "readership" && <Users className="size-4.5 text-foreground" />}
                    </div>
                  </div>
                  <CardTitle className="mt-4 font-serif text-xl font-semibold tracking-tight text-foreground">
                    {pillar.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="mt-3 p-0">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {pillar.description}
                  </p>
                  <div className="mt-6 border-t border-border pt-4">
                    <div className="font-serif text-2xl font-semibold text-foreground">
                      {pillar.metric}
                    </div>
                    <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                      {pillar.metricLabel}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}